import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Copy, KeyRound, RefreshCw, Search, X } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { PageHeader, SectionCard } from '@/components/admin/page';
import { api, ApiError } from '../../lib/api';
import {
  displayEmail,
  displayIdentifier,
  displayPersonName,
  displayReferralCode,
  displayTelegramHandle,
  displayWorkspaceName,
  formatDateTime,
  formatNumber,
  formatTime,
} from '../../lib/format';
import { useI18n } from '../../lib/i18n';
import { Pager } from '../../lib/Pager';

const PAGE_SIZE = 12;
const FETCH_LIMIT = 200;
const QUEUE_STATUS_FILTERS = ['all', 'pending_review', 'approved_pending_telegram'] as const;
const QUEUE_SORTS = ['newest', 'oldest', 'status'] as const;

type QueueStatusFilter = typeof QUEUE_STATUS_FILTERS[number];
type QueueSort = typeof QUEUE_SORTS[number];

interface ApplicationRow {
  id: string;
  email: string;
  displayName: string | null;
  telegramUsername: string | null;
  telegramChatId: string | null;
  requestedWorkspaceName: string | null;
  status: string;
  approvedTenantId: string | null;
  approvedTenantName: string | null;
  approvedTenantSlug: string | null;
  approvedRole: string | null;
  matchedUserId: string | null;
  reviewReason: string | null;
  createdAt: string;
  employeeNumber: string | null;
  nickname: string | null;
  tgName: string | null;
  referralCode: string | null;
}

interface GrantRow {
  id: string;
  tenantId: string;
  email: string;
  displayName: string | null;
  telegramUsername: string | null;
  telegramChatId: string | null;
  role: string;
  status: string;
  matchedUserId: string | null;
  notes: string | null;
  createdAt: string;
}

interface TenantOption {
  id: string;
  slug: string;
  name: string;
}

interface PairingCodeResult {
  code: string;
  expiresAt: string;
  expiresInSec: number;
}

interface OtpResult {
  developmentCode?: string;
  delivery?: { delivered?: boolean; skippedReason?: string };
}

interface ApprovalResponse {
  ok: boolean;
  applicationId: string;
  userId: string;
  tenantId: string;
  status: string;
  pairing?: PairingCodeResult;
  telegram?: OtpResult;
}

interface QueueRow {
  id: string;
  kind: 'grant' | 'application';
  email: string;
  displayName: string | null;
  workspace: string;
  role: string | null;
  status: string;
  note: string | null;
  telegramUsername: string | null;
  createdAt: string;
  grant?: GrantRow;
  application?: ApplicationRow;
}

const STATUS_KEY: Record<string, string> = {
  active: 'status.active',
  preapproved: 'status.preapproved',
  pending_review: 'status.pending_review',
  pending_telegram: 'status.pending_telegram',
  matched_pending_telegram: 'status.matched_pending_telegram',
  approved_pending_telegram: 'status.approved_pending_telegram',
  rejected: 'status.rejected',
  revoked: 'status.revoked',
  locked: 'status.locked',
  wipe_pending: 'status.wipe_pending',
};

const ROLE_KEY: Record<string, string> = {
  owner: 'role.owner',
  admin: 'role.admin',
  member: 'role.member',
  viewer: 'role.viewer',
};

function shortId(id: string | null): string {
  return id ? `${id.slice(0, 8)}…` : '-';
}

function EmptyRow({ colSpan, children }: { colSpan: number; children: ReactNode }) {
  return <TableRow><TableCell colSpan={colSpan} className="py-10 text-center text-muted-foreground">{children}</TableCell></TableRow>;
}

function employeeNumberFor(app: ApplicationRow): string | null {
  if (app.employeeNumber) return app.employeeNumber;
  if (app.email.endsWith('@sayknow.local')) return app.email.replace(/@sayknow\.local$/, '');
  return null;
}

function queueRowTone(status: string): string {
  if (status === 'pending_review') return 'border-l-amber-400/70 bg-amber-500/[0.03]';
  if (status === 'approved_pending_telegram') return 'border-l-blue-400/70 bg-blue-500/[0.03]';
  return 'border-l-transparent';
}

function statusRank(status: string): number {
  if (status === 'pending_review') return 0;
  if (status === 'approved_pending_telegram') return 1;
  return 2;
}

function searchableText(row: QueueRow): string {
  const app = row.application;
  const grant = row.grant;
  return [
    row.email,
    row.displayName,
    row.workspace,
    row.role,
    row.status,
    row.note,
    row.telegramUsername,
    app?.employeeNumber,
    app ? employeeNumberFor(app) : null,
    app?.nickname,
    app?.tgName,
    app?.referralCode,
    app?.telegramChatId,
    app?.approvedTenantId,
    app?.approvedTenantName,
    app?.approvedTenantSlug,
    app?.matchedUserId,
    grant?.telegramUsername,
    grant?.telegramChatId,
    grant?.matchedUserId,
  ].filter(Boolean).join(' ').toLowerCase();
}

export function AccessPage() {
  const { t } = useI18n();

  const statusLabel = (status: string): string => {
    const key = STATUS_KEY[status];
    return key ? t(key) : status;
  };

  const roleLabel = (role: string): string => {
    const key = ROLE_KEY[role];
    return key ? t(key) : role;
  };

  const statusBadge = (status: string) => {
    const warning = ['pending_review', 'pending_telegram', 'matched_pending_telegram', 'approved_pending_telegram', 'wipe_pending'];
    const danger = ['rejected', 'revoked', 'locked'];
    return <Badge variant={danger.includes(status) ? 'destructive' : warning.includes(status) ? 'warning' : 'success'}>{statusLabel(status)}</Badge>;
  };

  const [applications, setApplications] = useState<ApplicationRow[]>([]);
  const [grants, setGrants] = useState<GrantRow[]>([]);
  const [tenantOptions, setTenantOptions] = useState<TenantOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [queuePage, setQueuePage] = useState(0);
  const [selected, setSelected] = useState<QueueRow | null>(null);
  const [pairingCodes, setPairingCodes] = useState<Record<string, PairingCodeResult>>({});
  const [queueSearch, setQueueSearch] = useState('');
  const [queueStatus, setQueueStatus] = useState<QueueStatusFilter>('all');
  const [queueSort, setQueueSort] = useState<QueueSort>('newest');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [pendingApps, otpApps, grantRows, tenantList] = await Promise.all([
        api<{ applications: ApplicationRow[] }>(`/v1/admin/access/applications?status=pending_review&limit=${FETCH_LIMIT}`),
        api<{ applications: ApplicationRow[] }>(`/v1/admin/access/applications?status=approved_pending_telegram&limit=${FETCH_LIMIT}`),
        api<{ grants: GrantRow[] }>(`/v1/admin/access/grants?limit=${FETCH_LIMIT}`),
        api<{ tenants: TenantOption[] }>(`/v1/admin/tenants?limit=${FETCH_LIMIT}`),
      ]);
      setApplications([...pendingApps.applications, ...otpApps.applications]);
      setGrants(grantRows.grants);
      setTenantOptions(tenantList.tenants);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  useEffect(() => {
    setQueuePage(0);
  }, [queueSearch, queueStatus, queueSort]);

  const rememberPairingCode = (applicationId: string, pairing: PairingCodeResult) => {
    setPairingCodes((prev) => ({ ...prev, [applicationId]: pairing }));
  };

  const pairingAlert = (email: string, pairing: PairingCodeResult) => {
    alert([
      displayEmail(email) ?? email,
      `${t('access.detail.pairingTitle')}: ${pairing.code}`,
      `${t('common.expires')}: ${formatDateTime(pairing.expiresAt)}`,
    ].join('\n'));
  };

  const copyText = (value: string) => {
    void navigator.clipboard?.writeText(value);
  };

  const approveApplication = async (app: ApplicationRow) => {
    const targetTenantId = prompt(
      t('access.prompt.approveTenant'),
      app.approvedTenantId ?? '',
    );
    if (targetTenantId === null) return;
    try {
      const body = await api<ApprovalResponse>(`/v1/admin/access/applications/${app.id}/approve`, {
        method: 'POST',
        body: JSON.stringify({
          tenantId: targetTenantId.trim() || undefined,
          tenantName: app.requestedWorkspaceName ?? undefined,
          role: app.approvedRole ?? 'member',
        }),
      });
      if (body.pairing) {
        rememberPairingCode(app.id, body.pairing);
        pairingAlert(app.email, body.pairing);
      }
      if (body.telegram?.developmentCode || body.telegram?.delivery?.delivered === false) {
        showOtpResult(app.email, body.telegram);
      }
      await load();
    } catch (e) {
      alert(e instanceof ApiError ? e.message : (e as Error).message);
    }
  };

  const rejectApplication = async (app: ApplicationRow) => {
    const reason = prompt(t('access.prompt.rejectReason'), t('access.prompt.rejectDefault'));
    if (reason === null) return;
    try {
      await api(`/v1/admin/access/applications/${app.id}/reject`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      });
      await load();
    } catch (e) {
      alert(e instanceof ApiError ? e.message : (e as Error).message);
    }
  };

  const reopenApplication = async (app: ApplicationRow) => {
    const reason = prompt(t('access.prompt.reopenReason'), t('access.prompt.reopenDefault'));
    if (reason === null) return;
    try {
      await api(`/v1/admin/access/applications/${app.id}/reopen`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      });
      await load();
    } catch (e) {
      alert(e instanceof ApiError ? e.message : (e as Error).message);
    }
  };

  const resetPassword = async (userId: string, email: string) => {
    const shownEmail = displayEmail(email, userId) ?? email;
    const newPw = prompt(t('access.prompt.resetPassword', { email: shownEmail }));
    if (newPw === null) return;
    if (newPw.length < 8) {
      alert(t('access.prompt.passwordTooShort'));
      return;
    }
    try {
      await api(`/v1/admin/users/${userId}/reset-password`, {
        method: 'POST',
        body: JSON.stringify({ password: newPw }),
      });
      alert(t('access.prompt.passwordChanged', { email: shownEmail, pw: newPw }));
    } catch (e) {
      alert(e instanceof ApiError ? e.message : (e as Error).message);
    }
  };

  const showOtpResult = (email: string, body: OtpResult) => {
    const shownEmail = displayEmail(email) ?? email;
    if (body.developmentCode) {
      // 봇 미설정 또는 chat_id 없음 — 운영팀이 직접 전달해야 하는 코드
      alert(t('access.otp.manual', { email: shownEmail, code: body.developmentCode }));
    } else if (body.delivery?.delivered === false) {
      alert(t('access.otp.notDelivered', { email: shownEmail, reason: body.delivery.skippedReason ?? t('common.none') }));
    } else {
      alert(t('common.errOtpResent'));
    }
  };

  const resendApplicationOtp = async (app: ApplicationRow) => {
    if (!confirm(t('access.prompt.resendOtp', { email: displayEmail(app.email, app.matchedUserId ?? app.id) ?? app.email }))) return;
    try {
      const body = await api<{ developmentCode?: string; delivery?: { delivered?: boolean; skippedReason?: string } }>(
        `/v1/admin/access/applications/${app.id}/resend-otp`,
        { method: 'POST' },
      );
      showOtpResult(app.email, body);
      await load();
    } catch (e) {
      alert(e instanceof ApiError ? e.message : (e as Error).message);
    }
  };

  const issueApplicationPairingCode = async (app: ApplicationRow) => {
    try {
      const body = await api<{ ok: boolean } & PairingCodeResult>(
        `/v1/admin/access/applications/${app.id}/issue-pairing-code`,
        { method: 'POST' },
      );
      rememberPairingCode(app.id, body);
      pairingAlert(app.email, body);
    } catch (e) {
      alert(e instanceof ApiError ? e.message : (e as Error).message);
    }
  };

  const resendGrantOtp = async (grant: GrantRow) => {
    if (!confirm(t('access.prompt.resendOtp', { email: displayEmail(grant.email, grant.matchedUserId ?? grant.id) ?? grant.email }))) return;
    try {
      const body = await api<{ developmentCode?: string; delivery?: { delivered?: boolean; skippedReason?: string } }>(
        `/v1/admin/access/grants/${grant.id}/resend-otp`,
        { method: 'POST' },
      );
      showOtpResult(grant.email, body);
      await load();
    } catch (e) {
      alert(e instanceof ApiError ? e.message : (e as Error).message);
    }
  };

  const updateApplicationNote = async (app: ApplicationRow) => {
    const noteText = prompt(t('access.prompt.appNote'), app.reviewReason ?? '');
    if (noteText === null) return;
    try {
      await api(`/v1/admin/access/applications/${app.id}/note`, {
        method: 'PATCH',
        body: JSON.stringify({ note: noteText }),
      });
      await load();
    } catch (e) {
      alert(e instanceof ApiError ? e.message : (e as Error).message);
    }
  };

  const updateGrantNote = async (grant: GrantRow) => {
    const noteText = prompt(t('access.prompt.grantNote'), grant.notes ?? '');
    if (noteText === null) return;
    try {
      await api(`/v1/admin/access/grants/${grant.id}/note`, {
        method: 'PATCH',
        body: JSON.stringify({ note: noteText }),
      });
      await load();
    } catch (e) {
      alert(e instanceof ApiError ? e.message : (e as Error).message);
    }
  };

  const pendingApplications = applications.filter((a) => a.status === 'pending_review').length;
  const pendingOtp = applications.filter((a) => a.status === 'approved_pending_telegram').length
    + grants.filter((g) => g.status === 'matched_pending_telegram').length;

  // 사전 등록(grants) + 사용자 신청(applications) 통합 대기열. 최신 createdAt 순.
  const queue = useMemo<QueueRow[]>(() => {
    // 옵션 D: grant 흐름은 multi-tenant 잔재로 큐에서 제외. grants state 자체는 보존(미래 multi-tenant 복귀 대비).
    const aRows: QueueRow[] = applications.map((a) => ({
      id: `a:${a.id}`,
      kind: 'application',
      email: a.email,
      displayName: a.displayName,
      workspace: a.approvedTenantName ?? a.requestedWorkspaceName ?? '-',
      role: a.approvedRole,
      status: a.status,
      note: a.reviewReason,
      telegramUsername: a.telegramUsername,
      createdAt: a.createdAt,
      application: a,
    }));
    return aRows.sort((x, y) => y.createdAt.localeCompare(x.createdAt));
  }, [applications]);

  const filteredQueue = useMemo<QueueRow[]>(() => {
    const needle = queueSearch.trim().toLowerCase();
    const rows = queue.filter((row) => {
      if (queueStatus !== 'all' && row.status !== queueStatus) return false;
      if (!needle) return true;
      return searchableText(row).includes(needle);
    });
    return [...rows].sort((a, b) => {
      if (queueSort === 'oldest') return a.createdAt.localeCompare(b.createdAt);
      if (queueSort === 'status') {
        const byStatus = statusRank(a.status) - statusRank(b.status);
        if (byStatus !== 0) return byStatus;
      }
      return b.createdAt.localeCompare(a.createdAt);
    });
  }, [queue, queueSearch, queueStatus, queueSort]);

  const queueRows = filteredQueue.slice(queuePage * PAGE_SIZE, (queuePage + 1) * PAGE_SIZE);
  const queuePendingReview = queue.filter((r) => r.status === 'pending_review').length;
  const queuePendingTelegram = queue.filter((r) => r.status === 'approved_pending_telegram').length;
  const hasQueueControls = queueSearch.trim().length > 0 || queueStatus !== 'all' || queueSort !== 'newest';
  const resetQueueControls = () => {
    setQueueSearch('');
    setQueueStatus('all');
    setQueueSort('newest');
  };

  return (
    <div>
      <PageHeader
        title={t('access.title')}
        description={t('access.desc')}
        actions={<Button variant="outline" onClick={load} disabled={loading}><RefreshCw className={loading ? 'animate-spin' : ''} />{loading ? t('common.loading') : t('common.refresh')}</Button>}
      />

      {error && <Alert variant="destructive" className="mb-4"><AlertDescription>{error}</AlertDescription></Alert>}

      <div className="mb-4 flex flex-wrap gap-x-5 gap-y-1 text-xs text-muted-foreground">
        <span>{t('access.statsPendingReview')} <span className="font-medium text-foreground">{pendingApplications}</span></span>
        <span>{t('access.statsPendingOtp')} <span className="font-medium text-foreground">{pendingOtp}</span></span>
      </div>

      <div className="space-y-6">
        {/* 옵션 D 모델: 운영자는 사용자 직접 발급 X. 사용자 회원가입 → 워크스페이스 자동 생성 → admin은 승인/거부만. */}
        {/* 사용자 가입 신청 심사 대기열 */}
        <SectionCard
          title={t('access.queue.section')}
          description={t('access.queue.sectionDesc')}
          actions={<Badge variant="muted">{t('access.queue.resultCount', { shown: formatNumber(filteredQueue.length), total: formatNumber(queue.length) })}</Badge>}
        >
          <div className="mb-4 rounded-xl border bg-muted/10 p-4">
            <div className="grid gap-3 xl:grid-cols-[minmax(360px,1fr)_190px_170px_auto]">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={queueSearch}
                  onChange={(e) => setQueueSearch(e.target.value)}
                  placeholder={t('access.queue.searchPlaceholder')}
                  className="pl-9"
                />
              </div>
              <Select
                aria-label={t('access.queue.filterStatus')}
                value={queueStatus}
                onChange={(e) => setQueueStatus(e.target.value as QueueStatusFilter)}
              >
                <option value="all">{t('access.queue.filter.all')}</option>
                <option value="pending_review">{statusLabel('pending_review')}</option>
                <option value="approved_pending_telegram">{statusLabel('approved_pending_telegram')}</option>
              </Select>
              <Select
                aria-label={t('access.queue.sortLabel')}
                value={queueSort}
                onChange={(e) => setQueueSort(e.target.value as QueueSort)}
              >
                <option value="newest">{t('access.queue.sort.newest')}</option>
                <option value="oldest">{t('access.queue.sort.oldest')}</option>
                <option value="status">{t('access.queue.sort.status')}</option>
              </Select>
              <Button variant="outline" onClick={resetQueueControls} disabled={!hasQueueControls}>
                <X />{t('access.queue.reset')}
              </Button>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <QueueFilterChip
                label={t('access.queue.stat.total')}
                count={queue.length}
                active={queueStatus === 'all'}
                onClick={() => setQueueStatus('all')}
              />
              <QueueFilterChip
                label={statusLabel('pending_review')}
                count={queuePendingReview}
                active={queueStatus === 'pending_review'}
                onClick={() => setQueueStatus('pending_review')}
              />
              <QueueFilterChip
                label={statusLabel('approved_pending_telegram')}
                count={queuePendingTelegram}
                active={queueStatus === 'approved_pending_telegram'}
                onClick={() => setQueueStatus('approved_pending_telegram')}
              />
              <div className="rounded-full border bg-background px-3 py-1 text-xs text-muted-foreground">
                {t('access.queue.stat.filtered')} <span className="font-medium text-foreground">{formatNumber(filteredQueue.length)}</span>
              </div>
            </div>
          </div>
          <Table className="min-w-[1480px]">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[310px]">{t('access.apps.col.applicant')}</TableHead>
                <TableHead className="w-[390px]">{t('access.queue.col.signupInfo')}</TableHead>
                <TableHead className="w-[230px]">{t('access.queue.col.workspaceRole')}</TableHead>
                <TableHead className="w-[150px]">{t('common.status')}</TableHead>
                <TableHead className="w-[230px]">{t('common.note')}</TableHead>
                <TableHead className="w-[170px]">{t('access.detail.pairingTitle')}</TableHead>
                <TableHead className="w-[300px]">{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {queueRows.map((r) => (
                <TableRow key={r.id} className={`cursor-pointer border-l-2 hover:bg-accent/30 ${queueRowTone(r.status)}`} onClick={() => setSelected(r)}>
                  <TableCell className="align-top">
                    <div className="space-y-2">
                      <div>
                        <div className="font-medium text-foreground">
                          {displayEmail(r.email, r.application?.matchedUserId ?? r.grant?.matchedUserId ?? r.id)}
                        </div>
                        <div className="mt-0.5 text-xs text-muted-foreground">
                          {displayPersonName(r.displayName, r.email) ?? '-'}
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <Badge variant={r.kind === 'grant' ? 'muted' : 'warning'}>
                          {t(r.kind === 'grant' ? 'access.source.preregister' : 'access.source.application')}
                        </Badge>
                        {r.kind === 'grant' && (
                          <Badge variant="muted" className="text-[10px] opacity-60">{t('common.historyData')}</Badge>
                        )}
                      </div>
                      <div className="text-[11px] text-muted-foreground">{formatDateTime(r.createdAt)}</div>
                    </div>
                  </TableCell>
                  <TableCell className="align-top">
                    {r.kind === 'application' && r.application ? (
                      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                        <CompactField label={t('access.queue.field.employeeNumber')} value={displayIdentifier(employeeNumberFor(r.application), t('access.queue.field.employeeNumber'))} />
                        <CompactField label={t('access.queue.field.nickname')} value={displayPersonName(r.application.nickname, r.application.email)} />
                        <CompactField label={t('access.queue.field.tgName')} value={displayPersonName(r.application.tgName, r.application.email)} />
                        <CompactField label={t('access.queue.field.tgUsername')} value={displayTelegramHandle(r.application.telegramUsername)} />
                        <CompactField label={t('access.queue.field.referralCode')} value={displayReferralCode(r.application.referralCode)} />
                        <CompactField label={t('access.queue.field.telegramChatId')} value={displayIdentifier(r.application.telegramChatId, t('common.telegramChatId'))} />
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                        <CompactField label={t('access.queue.field.tgUsername')} value={displayTelegramHandle(r.grant?.telegramUsername)} />
                        <CompactField label={t('access.queue.field.telegramChatId')} value={displayIdentifier(r.grant?.telegramChatId, t('common.telegramChatId'))} />
                        <CompactField label={t('common.displayName')} value={displayPersonName(r.grant?.displayName, r.grant?.email)} />
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="align-top">
                    <div className="space-y-2">
                      <div className="text-sm text-foreground">
                        {r.kind === 'grant'
                          ? (displayWorkspaceName(tenantOptions.find((tn) => tn.id === r.workspace)?.name) ?? displayIdentifier(r.workspace, t('common.workspaceId')) ?? shortId(r.workspace))
                          : displayWorkspaceName(r.workspace)}
                      </div>
                      {r.role ? <Badge variant="muted">{roleLabel(r.role)}</Badge> : <span className="text-muted-foreground">-</span>}
                    </div>
                  </TableCell>
                  <TableCell className="align-top">{statusBadge(r.status)}</TableCell>
                  <TableCell className="align-top text-sm text-muted-foreground">
                    <div className="max-w-[230px] whitespace-normal break-words">{r.note ?? '-'}</div>
                  </TableCell>
                  <TableCell className="align-top">
                    {r.application && pairingCodes[r.application.id]
                      ? <code className="rounded-md bg-primary/10 px-2 py-1 text-xs text-primary">{pairingCodes[r.application.id]!.code}</code>
                      : <span className="text-muted-foreground">-</span>}
                  </TableCell>
                  <TableCell className="align-top" onClick={(e) => e.stopPropagation()}>
                    <div className="flex flex-wrap gap-2">
                      {r.kind === 'application' && r.application && (
                        <>
                          {(r.status === 'pending_review' || r.status === 'preapproved') && (
                            <>
                              <Button variant="success" size="sm" onClick={() => approveApplication(r.application!)}>{t('access.apps.approve')}</Button>
                              <Button variant="destructive" size="sm" onClick={() => rejectApplication(r.application!)}>{t('access.apps.reject')}</Button>
                            </>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => setSelected(r)}>{t('common.detail')}</Button>
                          <Button variant="outline" size="sm" onClick={() => updateApplicationNote(r.application!)}>{t('common.note')}</Button>
                          {r.status === 'rejected' && <Button variant="outline" size="sm" onClick={() => reopenApplication(r.application!)}>{t('access.apps.reopen')}</Button>}
                          {r.status === 'approved_pending_telegram' && <Button variant="outline" size="sm" onClick={() => resendApplicationOtp(r.application!)}>{t('access.grants.resendOtp')}</Button>}
                          {r.application.approvedTenantId && (
                            <Button variant="outline" size="sm" onClick={() => issueApplicationPairingCode(r.application!)}>
                              <KeyRound />{t('pairing.title')}
                            </Button>
                          )}
                        </>
                      )}
                      {r.kind === 'grant' && r.grant && (
                        <>
                          <Button variant="outline" size="sm" onClick={() => updateGrantNote(r.grant!)}>{t('common.note')}</Button>
                          {r.status === 'matched_pending_telegram' && <Button variant="outline" size="sm" onClick={() => resendGrantOtp(r.grant!)}>{t('access.grants.resendOtp')}</Button>}
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {queue.length === 0 && <EmptyRow colSpan={7}>{t('access.queue.empty')}</EmptyRow>}
              {queue.length > 0 && filteredQueue.length === 0 && <EmptyRow colSpan={7}>{t('access.queue.filteredEmpty')}</EmptyRow>}
            </TableBody>
          </Table>
          <Pager page={queuePage} pageSize={PAGE_SIZE} total={filteredQueue.length} onChange={setQueuePage} />
        </SectionCard>
      </div>

      <Dialog open={!!selected} onOpenChange={(v) => { if (!v) { setSelected(null); } }}>
        <DialogContent className="w-[min(1120px,calc(100vw-2rem))]" onClose={() => { setSelected(null); }}>
          {selected && (
            <div className="space-y-4">
              <div>
                <div className="text-lg font-semibold">
                  {displayEmail(selected.email, selected.application?.matchedUserId ?? selected.grant?.matchedUserId ?? selected.id)}
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  <Badge variant={selected.kind === 'grant' ? 'muted' : 'warning'}>
                    {t(selected.kind === 'grant' ? 'access.source.preregister' : 'access.source.application')}
                  </Badge>
                  {selected.kind === 'grant' && (
                    <Badge variant="destructive" className="text-[10px]">{t('common.historyData')}</Badge>
                  )}
                  {statusBadge(selected.status)}
                </div>
                {selected.kind === 'grant' && (
                  <div className="mt-2 rounded-md border border-amber-500/30 bg-amber-500/5 p-2 text-[11px] text-amber-700 dark:text-amber-400">
                    {t('access.grants.deprecatedNotice')}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                <DetailRow label={t('common.email')} value={displayEmail(selected.email, selected.application?.matchedUserId ?? selected.grant?.matchedUserId ?? selected.id)} />
                {/* email 이 <사번>@sayknow.local 패턴이면 사번 추출해서 별도 노출 */}
                {selected.email.endsWith('@sayknow.local') && (
                  <DetailRow
                    label={t('access.detail.employeeNumber')}
                    value={displayIdentifier(selected.email.replace(/@sayknow\.local$/, ''), t('access.detail.employeeNumber'))}
                  />
                )}
                <DetailRow
                  label={t('access.detail.displayName')}
                  value={displayPersonName(selected.displayName, selected.email)}
                />
                <DetailRow label={t('common.telegramAccount')} value={displayTelegramHandle(selected.telegramUsername)} />
                <DetailRow label={t('common.telegramChatId')} value={displayIdentifier((selected.kind === 'application' ? selected.application?.telegramChatId : selected.grant?.telegramChatId) ?? null, t('common.telegramChatId'))} />
                <DetailRow label={t('common.workspace')} value={selected.kind === 'grant'
                  ? (displayWorkspaceName(tenantOptions.find((tn) => tn.id === selected.workspace)?.name) ?? displayIdentifier(selected.workspace, t('common.workspaceId')))
                  : displayWorkspaceName(selected.workspace)}
                />
                {selected.kind === 'grant' && (
                  <DetailRow label={t('common.workspaceId')} value={displayIdentifier(selected.workspace, t('common.workspaceId'))} />
                )}
                <DetailRow label={t('common.role')} value={selected.role ? roleLabel(selected.role) : null} />
                <DetailRow label={t('common.created')} value={formatDateTime(selected.createdAt)} />
                {selected.kind === 'application' && selected.application && (
                  <>
                    <DetailRow label={t('access.detail.nickname')} value={displayPersonName(selected.application.nickname, selected.application.email)} />
                    <DetailRow label={t('access.detail.tgName')} value={displayPersonName(selected.application.tgName, selected.application.email)} />
                    <DetailRow label={t('access.detail.employeeNumber')} value={displayIdentifier(employeeNumberFor(selected.application), t('access.detail.employeeNumber'))} />
                    <DetailRow label={t('access.detail.referralCode')} value={displayReferralCode(selected.application.referralCode)} />
                    <DetailRow label={t('access.detail.requestedWorkspace')} value={displayWorkspaceName(selected.application.requestedWorkspaceName)} />
                    <DetailRow label={t('access.detail.approvedTenantName')} value={displayWorkspaceName(selected.application.approvedTenantName)} />
                    <DetailRow label={t('access.detail.approvedTenantSlug')} value={displayIdentifier(selected.application.approvedTenantSlug, t('common.slug'))} />
                    <DetailRow label={t('access.detail.matchedUser')} value={displayIdentifier(selected.application.matchedUserId, t('common.userId'))} />
                    <DetailRow label={t('access.detail.approvedTenant')} value={displayIdentifier(selected.application.approvedTenantId, t('common.workspaceId'))} />
                  </>
                )}
                {selected.kind === 'grant' && selected.grant && (
                  <DetailRow label={t('access.detail.matchedUser')} value={displayIdentifier(selected.grant.matchedUserId, t('common.userId'))} />
                )}
              </div>

              <div className="rounded-md border border-blue-500/20 bg-blue-500/5 p-3 text-[11px] text-muted-foreground">
                {t('access.detail.signupHint')}
              </div>

              {selected.note && (
                <div className="rounded-md border bg-muted/20 p-3">
                  <div className="text-xs font-medium text-muted-foreground mb-1">{t('common.note')}</div>
                  <div className="text-sm whitespace-pre-wrap">{selected.note}</div>
                </div>
              )}

              {/* 페어링 코드 — 승인 통과 후 운영자가 로컬 앱 연결 코드를 발급 */}
              <div className="rounded-md border border-primary/30 bg-primary/5 p-3">
                <div className="text-xs font-medium text-foreground mb-1">{t('access.detail.pairingTitle')}</div>
                {selected.kind === 'application' && selected.application?.approvedTenantId ? (
                  <div className="space-y-2">
                    {pairingCodes[selected.application.id] ? (
                      <div className="flex flex-wrap items-center gap-2">
                        <code className="rounded-md bg-background px-3 py-2 text-lg font-semibold tracking-[0.18em] text-foreground">
                          {pairingCodes[selected.application.id]!.code}
                        </code>
                        <Button size="sm" variant="outline" onClick={() => copyText(pairingCodes[selected.application!.id]!.code)}>
                          <Copy />{t('common.copy')}
                        </Button>
                        <Badge variant="success">
                          {t('common.expires')} {formatTime(pairingCodes[selected.application.id]!.expiresAt)}
                        </Badge>
                      </div>
                    ) : (
                      <div className="text-xs text-muted-foreground">{t('access.detail.pairingDesc')}</div>
                    )}
                    <Button size="sm" variant="outline" onClick={() => issueApplicationPairingCode(selected.application!)}>
                      <KeyRound />{t('pairing.title')}
                    </Button>
                  </div>
                ) : (
                  <div className="text-xs text-muted-foreground">{t('access.detail.pairingDesc')}</div>
                )}
              </div>

              {/* 비밀번호 — 평문은 못 보지만 재설정 가능. 매칭된 user 있을 때만 */}
              {(() => {
                const matchedUserId = selected.kind === 'application'
                  ? selected.application?.matchedUserId
                  : selected.grant?.matchedUserId;
                if (!matchedUserId) return null;
                return (
                  <>
                    <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3">
                      <div className="text-xs font-medium text-foreground mb-1">{t('access.detail.passwordTitle')}</div>
                      <div className="text-xs text-muted-foreground mb-2">{t('access.detail.passwordDesc')}</div>
                      <Button size="sm" variant="warning" onClick={() => resetPassword(matchedUserId, selected.email)}>
                        {t('access.detail.resetPasswordBtn')}
                      </Button>
                    </div>
                    <div className="mt-3 rounded-md border border-blue-500/30 bg-blue-500/5 p-3">
                      <div className="text-xs font-medium text-foreground mb-1">控制台锁定 PIN</div>
                      <div className="text-xs text-muted-foreground mb-2">用户忘记 4 位 PIN 时,在线设备(WebSocket 连接)立即解锁。离线设备需要下次上线时手动清除。</div>
                      <Button size="sm" variant="outline" onClick={async () => {
                        if (!confirm('该用户所有在线设备的控制台锁定 PIN을 즉시 해제하시겠습니까?')) return;
                        try {
                          const res = await api<{ sent: number; offline: number; message: string }>(
                            `/v1/admin/users/${matchedUserId}/reset-dashboard-pin`,
                            { method: 'POST' },
                          );
                          alert(`✓ ${res.message}`);
                        } catch (e) {
                          alert(e instanceof ApiError ? e.message : (e as Error).message);
                        }
                      }}>
                        解锁屏幕 PIN
                      </Button>
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DetailRow({ label, value, mono, small }: { label: string; value: string | null | undefined; mono?: boolean; small?: boolean }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`mt-0.5 break-all ${mono ? 'font-mono' : ''} ${small ? 'text-[11px]' : 'text-sm'}`}>
        {value || <span className="text-muted-foreground">—</span>}
      </div>
    </div>
  );
}

function CompactField({ label, value, mono }: { label: string; value: string | null | undefined; mono?: boolean }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`mt-0.5 break-all text-[13px] text-foreground ${mono ? 'font-mono' : ''}`}>
        {value || <span className="text-muted-foreground">—</span>}
      </div>
    </div>
  );
}

function QueueFilterChip({ label, count, active, onClick }: { label: string; count: number; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full border px-3 py-1 text-xs transition-colors ${
        active
          ? 'border-primary/40 bg-primary/10 text-primary'
          : 'border-border bg-background text-muted-foreground hover:bg-accent hover:text-accent-foreground'
      }`}
    >
      {label} <span className="font-medium">{formatNumber(count)}</span>
    </button>
  );
}
